'use client';

import { useEffect, useState } from 'react';

export const extractNumericValue = (label: string): number => {
  // Remove tudo exceto dígitos, vírgulas e pontos
  const cleaned = label.replace(/[^\d,.]/g, '');

  // Se tem vírgula, assume formato brasileiro (vírgula = decimal, ponto = milhar)
  // Exemplo: "20.000,00" -> remove pontos -> "20000,00" -> substitui vírgula -> "20000.00"
  if (cleaned.includes(',')) {
    // Remove pontos (separadores de milhar) e substitui vírgula por ponto
    const withoutThousands = cleaned.replace(/\./g, '');
    const normalized = withoutThousands.replace(',', '.');
    return parseFloat(normalized) || 0;
  }

  // Se tem ponto mas não vírgula, pode ser formato brasileiro sem decimais (20.000) ou americano (20000.00)
  // Verifica se parece formato brasileiro: múltiplos pontos (separadores de milhar)
  if (cleaned.includes('.')) {
    const parts = cleaned.split('.');
    // Se há múltiplas partes e todas as partes (exceto a primeira) têm 3 dígitos, é formato brasileiro
    // Exemplo: "20.000" -> ["20", "000"] -> última parte tem 3 dígitos
    // Exemplo: "1.234.567" -> ["1", "234", "567"] -> todas as partes após a primeira têm 3 dígitos
    const isBrazilianFormat =
      parts.length > 1 && parts.slice(1).every((part) => part.length === 3);

    if (isBrazilianFormat) {
      // Formato brasileiro sem decimais: 20.000 ou 1.234.567
      const withoutThousands = cleaned.replace(/\./g, '');
      return parseFloat(withoutThousands) || 0;
    }
    // Caso contrário, assume formato americano: 20000.00
    return parseFloat(cleaned) || 0;
  }

  // Apenas números sem separadores
  return parseFloat(cleaned) || 0;
};

type UsePrizeAnimationParams = {
  prizeLabel: string;
  duration?: number;
  delay?: number;
};

export const usePrizeAnimation = ({
  prizeLabel,
  duration = 2000,
  delay = 200,
}: UsePrizeAnimationParams) => {
  const [animatedValue, setAnimatedValue] = useState<number>(0);

  useEffect(() => {
    const targetValue = extractNumericValue(prizeLabel);
    if (targetValue === 0) return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (targetValue - startValue) * easeOut;

      setAnimatedValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedValue(targetValue);
      }
    };

    const timeout = setTimeout(() => {
      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timeout);
  }, [prizeLabel, duration, delay]);

  return { animatedValue };
};
